import { useCallback } from 'react';
import apiClient from '@/lib/apiClient';

interface OAuthAuthorizeResponse {
  redirectTo: string;
}

/**
 * Parses the short-lived auth code from the `token` query param of an `oauth/authorize`
 * `redirectTo` value. Handles absolute URLs, relative URLs, and trailing query params.
 */
export function parseAuthCodeFromRedirect(redirectTo: string): string | null {
  if (!redirectTo) {
    return null;
  }

  try {
    // Provide a base so both absolute and relative redirect URLs parse reliably. searchParams
    // URL-decodes the value and ignores any trailing #fragment.
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(redirectTo, base).searchParams.get('token');
  } catch {
    return null;
  }
}

/**
 * Exchanges the current user's bearer token for a short-lived Fundbox auth code via the FSS
 * OAuth authorize endpoint. The call is routed through the gateway by `apiClient` (the same
 * origin every other enterprise-ui request uses), so no cross-origin/CORS handling is needed
 * and the bearer token is injected automatically.
 *
 * @param clientId Environment-specific Fundbox OAuth client id.
 * @returns The code to set on the Fundbox mount div's `data-fundbox-auth-token` attribute.
 */
export async function fetchFundboxAuthCode(clientId: string): Promise<string> {
  const { redirectTo } = await apiClient
    .post('api/auth/oauth/authorize', { searchParams: { clientId } })
    .json<OAuthAuthorizeResponse>();

  const code = parseAuthCodeFromRedirect(redirectTo);
  if (!code) {
    throw new Error('Fundbox auth code was missing from the oauth/authorize response');
  }

  return code;
}

export interface UseFundboxAuthCodeResult {
  /** Fetches a fresh short-lived Fundbox auth code for the given OAuth client id. */
  fetchAuthCode: (clientId: string) => Promise<string>;
}

/**
 * Exposes the imperative Fundbox auth-code exchange. Kept as an on-demand call (rather than a
 * cached React Query hook) because a fresh, short-lived code is fetched right before the Fuse
 * embed launches. See the fundbox auth-token analysis doc for the planned caching follow-up.
 */
function useFundboxAuthCode(): UseFundboxAuthCodeResult {
  const fetchAuthCode = useCallback((clientId: string) => fetchFundboxAuthCode(clientId), []);

  return { fetchAuthCode };
}

export default useFundboxAuthCode;
